import { getAssistance } from "../lib/admin-assistance";
import { useAuth } from "../lib/auth";
import { AssistanceBanner } from "../features/admin-workspace/AssistanceControls";
import { useEffect, useMemo } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from "react-router";
import { canAccessAdmin, canAccessProvider } from "../lib/api";
import { AdminConsoleLayout } from "../features/admin-console/AdminConsoleLayout";
import { adminConsoleRoutes } from "../features/admin-console/routes";
import { ForgotPasswordPage } from "../features/auth/ForgotPasswordPage";
import { ProviderRegistrationPage } from "../features/auth/ProviderRegistrationPage";
import { ResetPasswordPage } from "../features/auth/ResetPasswordPage";
import { SetPasswordPage } from "../features/auth/SetPasswordPage";
import { SignInPage } from "../features/auth/SignInPage";
import { VerifyEmailPage } from "../features/auth/VerifyEmailPage";
import { LandingPage } from "../features/landing/LandingPage";
import { JoinPage } from "../features/onboarding/JoinPage";
import { TermsPage } from "../features/legal/TermsPage";
import { PrivacyPage } from "../features/legal/PrivacyPage";
import { RefundPage } from "../features/legal/RefundPage";
import { AccountDeletionPage } from "../features/legal/AccountDeletionPage";
import { ProviderLayout } from "../features/provider-workspace/ProviderLayout";
import { BusinessSection } from "../features/provider-workspace/pages/BusinessSection";
import { CustomersSection } from "../features/provider-workspace/pages/CustomersSection";
import { DashboardSection } from "../features/provider-workspace/pages/DashboardSection";
import { FloorSection } from "../features/provider-workspace/pages/FloorSection";
import { RegisterSection } from "../features/provider-workspace/pages/RegisterSection";
import { ScheduleSection } from "../features/provider-workspace/pages/ScheduleSection";
import { VenueSetupSection } from "../features/provider-workspace/pages/VenueSetupSection";
import { TournamentsSection } from "../features/provider-tournaments/TournamentsSection";
import { OrdersSection } from "../features/provider-orders/OrdersSection";
import { MenuStockSection } from "../features/provider-menu/MenuStockSection";
import { AccessDeniedPage } from "../features/workspace/AccessDeniedPage";
import { ProtectedRoute } from "./ProtectedRoute";

function RouteScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (location.hash) {
      const target = document.getElementById(location.hash.slice(1));
      if (target) {
        target.scrollIntoView();
      }
      return;
    }

    window.scrollTo({ left: 0, top: 0, behavior: "auto" });
  }, [location.hash, location.pathname, location.search]);

  return null;
}

function RootLayout() {
  useAuth();
  return (
    <div className={getAssistance() ? "assistance-root" : undefined}>
      <RouteScrollManager />
      <AssistanceBanner />
      <Outlet />
    </div>
  );
}

export function createAppRouter() {
  const providerDashboardPath = "/provider/dashboard";
  const providerRoute = {
    path: "/provider",
    element: (
      <ProtectedRoute allow={canAccessProvider}><ProviderLayout /></ProtectedRoute>
    ),
    children: [
      { index: true, element: <Navigate replace to={providerDashboardPath} /> },
      { path: "dashboard", element: <DashboardSection /> },
      { path: "floor", element: <FloorSection /> },
      { path: "orders", element: <OrdersSection /> },
      { path: "menu", element: <MenuStockSection /> },
      { path: "schedule", element: <ScheduleSection /> },
      { path: "register", element: <RegisterSection /> },
      { path: "customers", element: <CustomersSection /> },
      { path: "venue-setup", element: <VenueSetupSection /> },
      { path: "tournaments", element: <TournamentsSection /> },
      { path: "business", element: <BusinessSection /> },
      { path: "*", element: <Navigate replace to={providerDashboardPath} /> },
    ],
  };

  return createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <LandingPage /> },
      { path: "/join", element: <JoinPage /> },
      { path: "/sign-in", element: <SignInPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
      { path: "/set-password", element: <SetPasswordPage /> },
      { path: "/provider-register", element: <ProviderRegistrationPage /> },
      { path: "/verify-email", element: <VerifyEmailPage /> },
      { path: "/access-denied", element: <AccessDeniedPage /> },
      { path: "/legal/terms", element: <TermsPage /> },
      { path: "/legal/privacy", element: <PrivacyPage /> },
      { path: "/legal/refund", element: <RefundPage /> },
      { path: "/legal/account-deletion", element: <AccountDeletionPage /> },
      {
        path: "/admin",
        element: <ProtectedRoute allow={canAccessAdmin}><AdminConsoleLayout /></ProtectedRoute>,
        children: adminConsoleRoutes,
      },
      providerRoute,
      { path: "*", element: <Navigate replace to="/" /> },
    ],
  },
  ]);
}

export function AppRouter() {
  const router = useMemo(() => createAppRouter(), []);
  return <RouterProvider router={router} />;
}
